// Passkey (WebAuthn) browser-side helpers. The server speaks the spec's
// JSON format (base64url strings); navigator.credentials speaks
// ArrayBuffers — these helpers translate both ways.

function base64urlToBuffer(value) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded);
  const buffer = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    buffer[i] = raw.charCodeAt(i);
  }
  return buffer.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let raw = '';
  for (let i = 0; i < bytes.length; i++) {
    raw += String.fromCharCode(bytes[i]);
  }
  return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Feature flag from the school's config (meta tag rendered by the
// server). Off = no passkey UI anywhere, regardless of browser support.
export function passkeysEnabled() {
  if (typeof document === 'undefined') {
    return false;
  }
  const meta = document.head.querySelector('meta[name="passkey-enabled"]');

  return !!meta && meta.getAttribute('content') === '1';
}

// "Can this page offer passkeys": the school has the feature on AND the
// browser can do WebAuthn. Every passkey entry point keys off this.
export function passkeysSupported() {
  return (
    passkeysEnabled() &&
    typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined
  );
}

// options — the JSON produced by the server for credentials.create()
export async function createPasskey(options) {
  const publicKey = { ...options };
  publicKey.challenge = base64urlToBuffer(options.challenge);
  publicKey.user = { ...options.user, id: base64urlToBuffer(options.user.id) };
  publicKey.excludeCredentials = (options.excludeCredentials || []).map(c => ({
    ...c,
    id: base64urlToBuffer(c.id)
  }));

  const credential = await navigator.credentials.create({ publicKey });

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(
        credential.response.attestationObject
      ),
      transports:
        typeof credential.response.getTransports === 'function'
          ? credential.response.getTransports()
          : []
    },
    clientExtensionResults: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {}
  };
}

// WebAuthn Signal API: tell the browser a credential is unknown to the
// server so its password manager drops the ghost entry. Supported in
// modern Chrome; harmless no-op elsewhere.
export async function signalUnknownPasskey(credentialIdB64url) {
  try {
    if (
      window.PublicKeyCredential &&
      typeof PublicKeyCredential.signalUnknownCredential === 'function'
    ) {
      await PublicKeyCredential.signalUnknownCredential({
        rpId: window.location.hostname,
        credentialId: credentialIdB64url
      });

      return true;
    }
  } catch (e) {
    // Cleaning is best-effort only.
  }

  return false;
}

// Signal API, part two: report the full list of valid credential ids
// for a user so the password manager can drop everything else.
export async function signalAcceptedPasskeys(userId, credentialIds) {
  try {
    if (
      window.PublicKeyCredential &&
      typeof PublicKeyCredential.signalAllAcceptedCredentials === 'function'
    ) {
      await PublicKeyCredential.signalAllAcceptedCredentials({
        rpId: window.location.hostname,
        userId: btoa(String(userId))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, ''),
        allAcceptedCredentialIds: credentialIds
      });

      return true;
    }
  } catch (e) {
    // Best-effort sync only.
  }

  return false;
}

// True when the browser can offer a passkey right inside the login
// field's autofill dropdown (conditional UI).
export async function conditionalUiAvailable() {
  return (
    passkeysSupported() &&
    typeof PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
    (await PublicKeyCredential.isConditionalMediationAvailable())
  );
}

// options — the JSON produced by the server for credentials.get().
// extra: { mediation: 'conditional', signal: AbortSignal } for the
// autofill flow; omit for the classic modal prompt.
export async function getPasskey(options, extra = {}) {
  const publicKey = { ...options };
  publicKey.challenge = base64urlToBuffer(options.challenge);
  publicKey.allowCredentials = (options.allowCredentials || []).map(c => ({
    ...c,
    id: base64urlToBuffer(c.id)
  }));

  const credential = await navigator.credentials.get({
    publicKey,
    ...extra
  });

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64url(
        credential.response.authenticatorData
      ),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : null
    },
    clientExtensionResults: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {}
  };
}
